/*

Erklaerung:
Das original TelInput Widget ist in Safari kaputt. Statt das Input unter Safari komplett zu deaktivieren,
ersetzen wir es mit einer vereinfachten Dummy-Implementation. Diese macht nur grundlegende Validierung
und nutzt ein einfaches HTML tel Input Element. Das ist kurzfristig die bessere Loesung.

Um zu verhindern, dass die Anwendung crasht wenn die Safari-Implementierung Methoden/Properties
der Original-Implementierung nicht hat, verwenden wir einen Proxy. Der Proxy warnt vor fehlenden
Methoden und gibt sichere Fallback-Werte zurueck.
*/

import PhoneInputTelInput from "./PhoneInputTelInput";
import PhoneInputSafari from "./PhoneInputSafari/PhoneInputSafari";

/**
 * Erstellt einen Proxy, der warnt wenn der targetInstance Eigenschaften oder Methoden fehlen,
 * die auf dem canonicalPrototype existieren. Das ist die Basis-Implementierung.
 */
function createPolyfillProxy<T extends object>(
    targetInstance: T,
    canonicalPrototype: object
): T {

    const handler: ProxyHandler<T> = {
        /**
         * Interceptiert Eigenschaftszugriff: proxy.someProperty
         */
        get(target, prop, receiver) {
            // Pruefe ob die Eigenschaft auf dem target existiert (PhoneInputSafari)
            if (prop in target) {
                return Reflect.get(target, prop, receiver);
            }

            // Es ist nicht auf dem target.
            // Pruefe ob es auf der Basis (PhoneInputTelInput) existieren sollte
            if (prop in canonicalPrototype) {
                // Fehler: Die Basis hat es, aber die Safari-Implementierung nicht
                console.warn(
                    `[Polyfill Warning] Methode oder Eigenschaft "${String(prop)}" fehlt auf ${target.constructor.name} Implementierung.`
                );

                // "Mach nichts" - gebe eine sichere Fallback-Funktion oder undefined zurueck
                // um Crashes zu verhindern
                const propType = typeof (canonicalPrototype as any)[prop];
                if (propType === 'function') {
                    return () => { /* nix machen */ };
                } else {
                    return undefined;
                }
            }

            // Eigenschaft existiert weder auf target noch auf Basis
            return undefined;
        },

        /**
         * Interceptiert Eigenschaftszuweisung: proxy.someProperty = value
         */
        set(target, prop, value, receiver) {
            // Pruefe ob die Eigenschaft auf dem target existiert (PhoneInputSafari)
            if (prop in target) {
                return Reflect.set(target, prop, value, receiver);
            }

            // Es ist nicht auf dem target.
            // Pruefe ob es auf der Basis (PhoneInputTelInput) existieren sollte
            if (prop in canonicalPrototype) {
                // Fehler: Versuch eine fehlende Eigenschaft zu setzen
                console.warn(
                    `[Polyfill Warning] Versuch "${String(prop)}" zu setzen, was auf ${target.constructor.name} Implementierung fehlt. Operation uebersprungen.`
                );

                // "Mach nichts" - gebe true zurueck um Erfolg zu signalisieren
                return true;
            }

            // Neue Eigenschaft die nicht auf Basis ist - erlaube sie
            return Reflect.set(target, prop, value, receiver);
        },

        /**
         * Interceptiert den 'in' Operator: "someProperty" in proxy
         */
        has(target, prop) {
            // Melde dass der Proxy die Eigenschaft "hat" wenn sie auf target ODER
            // auf dem Basis-Prototype existiert
            return (prop in target) || (prop in canonicalPrototype);
        }
    };

    return new Proxy(targetInstance, handler);
}


// Erkenne ob der Browser Safari ist
const isSafari = (): boolean => {
    const ua = navigator.userAgent;
    const vendor = navigator.vendor;
    const hasSafariString = ua.includes('Safari');

    // Schliesse andere WebKit-Browser aus die auch "Safari" im UA-String haben
    const isChrome = ua.includes('Chrome') || ua.includes('CriOS');
    const isEdge = ua.includes('Edg');
    const isFirefoxOnIOS = ua.includes('FxiOS');

    let res= (
        vendor?.includes('Apple') &&
        hasSafariString &&
        !isChrome &&
        !isEdge &&
        !isFirefoxOnIOS
    );

    return res;
};

// Factory-Funktion die je nach Browser unterschiedliche Implementierungen erstellt
function PhoneInputFactory(...args): MdsLax<InstanceType<typeof PhoneInputTelInput>>{
    if (isSafari()){
        // Fuer Safari: Erstelle die vereinfachte Dummy-Implementierung mit Proxy-Schutz
        let safariInstance = Reflect.construct(PhoneInputSafari, args) as MdsLax<InstanceType<typeof PhoneInputTelInput>>;
        return createPolyfillProxy(
            safariInstance,
            PhoneInputTelInput.prototype
        );
    }else{
        // Fuer alle anderen Browser: Verwende die vollstaendige Original-Implementierung
        return Reflect.construct(PhoneInputTelInput, args) as MdsLax<InstanceType<typeof PhoneInputTelInput>>;
    }
}

//wir geben unsere Factory funktion als ctor fuer typescript aus (js ist es egal, funktioniert so oder so)

type PhoneInput__mdsbase = InstanceType<typeof PhoneInputTelInput>;
interface PhoneInput extends PhoneInput__mdsbase { }; //damit auch der export den richtigen typ bekommt und alles so funktioniert als waere es eine normale klasse...
type PhoneInputConstructor = new (...args: any[]) => MdsLax<InstanceType<typeof PhoneInputTelInput>>; //typen von orginalen klasse verwenden
const PhoneInput = PhoneInputFactory as unknown as PhoneInputConstructor;
export default PhoneInput; //export als orginales PhoneInput damit der Input ueberall global funktioniert
